import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { api } from '@/lib/api'
import { isAuthenticated, setAuthState } from '@/lib/auth-store'
import extensionSyncStatusGraphic from '@/assets/landing/extension-sync-status.svg'
import styles from './login.module.css'

export const Route = createFileRoute('/login')({
  beforeLoad: () => {
    if (isAuthenticated()) {
      throw redirect({ to: '/home' })
    }
  },
  component: LoginRoute,
})

const loginSchema = z.object({
  email: z.string().trim().email(),
  password: z.string().min(8),
})

function LoginRoute() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    setErrorMessage(null)

    const parseResult = loginSchema.safeParse({
      email,
      password,
    })

    if (!parseResult.success) {
      setErrorMessage('Enter a valid email and password.')
      return
    }

    setIsSubmitting(true)

    try {
      const response = await api.login(parseResult.data)
      setAuthState(response.user, response.session)
      await navigate({ to: '/home' })
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Login failed.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className={styles.layout}>
      <aside className={styles.visualPanel}>
        <p className={styles.kicker}>Return To Workspace</p>
        <h1 className={styles.visualTitle}>Pick up your weekly content routine.</h1>
        <p className={styles.visualBody}>
          Re-enter your workspace to generate drafts, review engagement signals, and prioritize warm
          lead outreach from one flow.
        </p>
        <img
          src={extensionSyncStatusGraphic}
          alt="Workspace sync status visual with extension connectivity indicators"
          className={styles.visualImage}
          loading="lazy"
        />
        <div className={styles.visualPoints}>
          <p>Voice-matched drafts</p>
          <p>Scheduling cadence</p>
          <p>Warm lead priorities</p>
        </div>
      </aside>

      <article className={styles.panel}>
        <h2 className={styles.title}>Welcome back</h2>
        <p className={styles.subtitle}>Sign in and continue where you left off.</p>
        <p className={styles.meta}>New accounts get a 14-day trial immediately at signup.</p>

        <form className={styles.form} onSubmit={handleSubmit}>
          <label className={styles.field}>
            <span>Email</span>
            <input
              type="email"
              value={email}
              autoComplete="email"
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>

          <label className={styles.field}>
            <span>Password</span>
            <input
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>

          {errorMessage && <p className={styles.error}>{errorMessage}</p>}

          <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
            {isSubmitting ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <p className={styles.footer}>
          New to StatureBoost? <Link to="/signup">Create your account</Link>.
        </p>
      </article>
    </section>
  )
}
